import React, { useState, useEffect, useRef, useCallback } from 'react';
import DynamicFontAwesomeIcon from '../../DynamicFontAwesomeIcon';
import { apiRequest } from '../../../../services/APIRequest';
import { useOrganization } from '../../../../context/OrganizationContext';
import { 
	categorizeFileType, 
	isImageFile, 
	FILE_CATEGORIES,
	extractFileIdentification,
	setupMarqueeAnimation,
	validateFile,
	VALIDATION_ERROR_DISPLAY_TIME,
	ERROR_FADE_OUT_TIME,
	PROGRESS_MIN_VISIBLE
} from './helpers';
import styles from './FileDropzone.module.scss';

/**
 * FileItem component for individual file upload with validation and progress tracking
 * 
 * @param {Object} props - Component props
 * @param {Object} props.fileObject - FileObject with file, fileHash, key, validation, identification, uploadResult
 * @param {Array} props.allFiles - Array of all FileObjects for duplicate checking
 * @param {number} props.maxFiles - Maximum number of files allowed
 * @param {number} props.maxFileSize - Maximum file size in MB (0 = unlimited)
 * @param {string} props.accept - Accepted file types (MIME types)
 * @param {string} props.uploadUrl - URL to upload files to
 * @param {string|number} props.refId - Optional reference ID
 * @param {string} props.refType - Optional reference type
 * @param {string} props.otk - Organization token
 * @param {function} props.onRemove - Callback to remove file from array
 * @param {function} props.onValidationChange - Callback when validation status changes
 * @param {function} props.onUploadComplete - Callback when upload completes successfully (key, identification, uploadResult)
 */
const FileItem = ({ 
	fileObject, 
	allFiles, 
	maxFiles, 
	maxFileSize = 0,
	accept, 
	uploadUrl, 
	refId, 
	refType, 
	otk, 
	onRemove, 
	onValidationChange, 
	onUploadComplete 
}) => {
	// Internal state
	const [validationError, setValidationError] = useState(null);
	const [uploadError, setUploadError] = useState(null);
	const [isRemoving, setIsRemoving] = useState(false);
	const [uploadStatus, setUploadStatus] = useState('pending'); // pending, uploading, completed, error
	const [uploadProgress, setUploadProgress] = useState(0);
	
	// Refs
	const uploadController = useRef(null);
	const fileNameRef = useRef(null);
	const isMounted = useRef(true);
	const uploadFileRef = useRef(null);
	const onUploadCompleteRef = useRef(onUploadComplete);
	
	// Get organization from context
	const organization = useOrganization();
	const finalOtk = otk || organization?.otk;
	
	// File preview state - always create blob URL for all files
	const [previewUrl, setPreviewUrl] = useState(null);
	const [blobUrl, setBlobUrl] = useState(null);
	
	// Generate blob URL for all files (for preview functionality)
	useEffect(() => {
		const url = URL.createObjectURL(fileObject.file);
		setBlobUrl(url);
		
		// Also set preview URL for images
		if (isImageFile(fileObject.file)) {
			setPreviewUrl(url);
		}
		
		return () => {
			URL.revokeObjectURL(url);
		};
	}, [fileObject.file]);
	
	// Setup marquee animation for filename
	useEffect(() => {
		if (fileNameRef.current) {
			const textElement = fileNameRef.current.querySelector('.marquee-content');
			setupMarqueeAnimation(textElement, fileNameRef.current);
		}
	}, [fileObject.file.name]);
	
	// Cleanup on unmount
	useEffect(() => {
		return () => {
			isMounted.current = false;
			if (uploadController.current) {
				uploadController.current.abort();
			}
		};
	}, []);
	
	// Validation function
	const validateFileItem = useCallback(() => {
		const validation = validateFile(fileObject.file, {
			accept,
			allFiles: allFiles.filter(f => f.key !== fileObject.key), // Exclude current file
			maxFiles,
			maxFileSize,
			fileHash: fileObject.fileHash,
		});
		
		if (!validation.isValid) {
			setValidationError(validation.error);
			return false;
		}
		
		return true;
	}, [fileObject, allFiles, maxFiles, maxFileSize, accept]);
	
	// Upload function - stable reference with useRef
	const uploadFile = useCallback(async () => {
		if (!finalOtk) {
			setUploadError('Keine Berechtigung für Upload');
			setUploadStatus('error');
			return;
		}
		
		try {
			setUploadStatus('uploading');
			setUploadProgress(0);
			
			// Create new AbortController for this upload
			uploadController.current = new AbortController();
			
			const formData = new FormData();
			formData.append('file[0]', fileObject.file);
			
			if (refId) formData.append('refId', refId);
			if (refType) formData.append('refType', refType);
			
			const response = await apiRequest(uploadUrl, {
				method: 'POST',
				data: formData,
				otk: finalOtk,
				signal: uploadController.current.signal,
				onUploadProgress: (progressEvent) => {
					if (progressEvent.lengthComputable) {
						const percentCompleted = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total
						);
						const visibleProgress = Math.max(PROGRESS_MIN_VISIBLE, percentCompleted);
						setUploadProgress(visibleProgress);
					}
				}
			});
			
			if (!isMounted.current) return;
			
			if (response.success) {
				const fileIdentification = extractFileIdentification(response, fileObject.file);
				
				// Check if we actually got a valid identification
				if (!fileIdentification) {
					setUploadError('Keine gültige Datei-Identifikation erhalten');
					setUploadStatus('error');
					
					// Mark file as invalid if identification extraction fails
					if (onValidationChange) {
						onValidationChange(fileObject.key, false);
					}
					return;
				}
				
				setUploadStatus('completed');
				setUploadProgress(100);
				
				// Mark file as fully validated after successful upload
				if (onValidationChange) {
					onValidationChange(fileObject.key, true);
				}
				
				// Notify parent of successful upload with key, identification and full response
				if (onUploadCompleteRef.current) {
					onUploadCompleteRef.current(fileObject.key, fileIdentification, response);
				}
			} else {
				throw new Error(response.message || 'Upload failed');
			}
			
		} catch (error) {
			if (!isMounted.current) return;
			
			if (error.name === 'AbortError') {
				// Upload was cancelled, don't set error state
				return;
			}
			
			setUploadError(error.message || 'Upload fehlgeschlagen');
			setUploadStatus('error');
			
			// Mark file as invalid if upload fails
			if (onValidationChange) {
				onValidationChange(fileObject.key, false);
			}
		}
	}, [fileObject.file, fileObject.key, finalOtk, uploadUrl, refId, refType, onValidationChange]);
	
	// Store upload function in ref to avoid dependency issues
	useEffect(() => {
		uploadFileRef.current = uploadFile;
		onUploadCompleteRef.current = onUploadComplete;
	}, [uploadFile, onUploadComplete]);
	
	// Retry upload function - simple approach
	const retryUpload = useCallback(() => {
		setUploadError(null);
		setUploadStatus('pending');
		setUploadProgress(0);
		
		// Reset validation status during retry
		if (onValidationChange) {
			onValidationChange(fileObject.key, false);
		}
		
		// Call upload directly using ref
		if (uploadFileRef.current) {
			uploadFileRef.current();
		}
	}, [fileObject.key, onValidationChange]);
	
	// Remove the problematic useEffect that was causing loops
	
	// Auto-remove timer for validation errors
	useEffect(() => {
		if (validationError) {
			const displayTimer = setTimeout(() => {
				setIsRemoving(true);
				
				// After fade-out: remove self
				const removeTimer = setTimeout(() => {
					onRemove(fileObject.key);
				}, ERROR_FADE_OUT_TIME);
				
				return () => clearTimeout(removeTimer);
			}, VALIDATION_ERROR_DISPLAY_TIME);
			
			return () => clearTimeout(displayTimer);
		}
	}, [validationError, fileObject.key, onRemove]);
	
	// Start validation and upload on mount (only once per file)
	useEffect(() => {
		// Don't run if already uploading/completed/error
		if (uploadStatus !== 'pending') return;
		
		const isValid = validateFileItem();
		
		// Only notify parent of initial validation status if file is invalid
		// Valid files will be marked as validated after successful upload
		if (onValidationChange && !isValid) {
			onValidationChange(fileObject.key, false);
		}
		
		if (isValid && uploadFileRef.current) {
			// Start upload after successful validation using ref
			uploadFileRef.current();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fileObject.key]); // Only depend on fileObject.key to run once per file
		// Handle remove button click with fade-out animation
	const handleRemove = useCallback(() => {
		if (uploadController.current) {
			uploadController.current.abort();
		}
		
		// Start fade-out animation
		setIsRemoving(true);
		
		// Remove file after animation completes
		setTimeout(() => {
			onRemove(fileObject.key);
		}, ERROR_FADE_OUT_TIME);
	}, [fileObject.key, onRemove]);

	// Get file icon based on category
	const getFileIcon = () => {
		const category = categorizeFileType(fileObject.file);
		
		switch (category) {
			case FILE_CATEGORIES.VIDEO:
				return { iconName: 'file-video', className: 'fileIcon--video' };
			case FILE_CATEGORIES.AUDIO:
				return { iconName: 'file-audio', className: 'fileIcon--audio' };
			case FILE_CATEGORIES.PDF:
				return { iconName: 'file-pdf', className: 'fileIcon--pdf' };
			case FILE_CATEGORIES.ARCHIVE:
				return { iconName: 'file-archive', className: 'fileIcon--archive' };
			case FILE_CATEGORIES.WORD:
				return { iconName: 'file-word', className: 'fileIcon--word' };
			case FILE_CATEGORIES.EXCEL:
				return { iconName: 'file-excel', className: 'fileIcon--excel' };
			case FILE_CATEGORIES.POWERPOINT:
				return { iconName: 'file-powerpoint', className: 'fileIcon--powerpoint' };
			case FILE_CATEGORIES.CSV:
				return { iconName: 'file-csv', className: 'fileIcon--csv' };
			case FILE_CATEGORIES.CODE:
				return { iconName: 'file-code', className: 'fileIcon--code' };
			case FILE_CATEGORIES.DMG:
				return { iconName: 'circle-down', className: 'fileIcon--dmg' };
			case FILE_CATEGORIES.WINDOWS:
				return { iconName: 'desktop', className: 'fileIcon--windows' };
			default:
				return { iconName: 'file-alt', className: 'fileIcon--default' };
		}
	};
	
	// Render status icon or progress indicator
	const renderStatus = () => {
		switch (uploadStatus) {
			case 'pending':
				return (
					<DynamicFontAwesomeIcon 
						iconName="spinner" 
						className={styles.statusIcon || styles.statusIconFallback || 'statusIcon'}
						data-icon="spinner"
						spin
					/>
				);
			case 'uploading':
				return (
					<div 
						className={styles.circularProgress || styles.circularProgressFallback || 'circularProgress'}>
						<div 
							className={styles.circularProgressIndicator || styles.circularProgressIndicatorFallback || 'circularProgressIndicator'}
							style={{
								background: `conic-gradient(var(--bs-primary) ${uploadProgress}%, transparent ${uploadProgress}%)`
							}}
						/>
						<div 
							className={styles.circularProgressText || styles.circularProgressTextFallback || 'circularProgressText'}>
							{uploadProgress}%
						</div>
					</div>
				);
			case 'completed':
				return (
					<DynamicFontAwesomeIcon 
						iconName="check-circle" 
						className={styles.statusIcon || styles.statusIconFallback || 'statusIcon'}
						data-icon="check-circle"
					/>
				);
			case 'error':
				return (
					<DynamicFontAwesomeIcon 
						iconName="exclamation-circle" 
						className={styles.statusIcon || styles.statusIconFallback || 'statusIcon'}
						data-icon="exclamation-circle"
					/>
				);
			default:
				return null;
		}
	};
	
	const fileIcon = getFileIcon();
	
	return (
		<div 
			className={`${styles.filePreview || styles.filePreviewFallback || 'filePreview'} ${isRemoving ? (styles.removing || 'removing') : ''}`}
			style={{
				// Set CSS custom property for fade-out animation duration
				'--fade-out-duration': `${ERROR_FADE_OUT_TIME}ms`
			}}>
			<div 
				className={styles.filePreviewInner || styles.filePreviewInnerFallback || 'filePreviewInner'}>
				{/* Background: Preview image or file icon */}
				{previewUrl ? (
					<img 
						src={previewUrl} 
						alt={fileObject.file.name}
						className={styles.previewImage || styles.previewImageFallback || 'previewImage'}
					/>
				) : (
					<div 
						className={styles.iconContainer || styles.iconContainerFallback || 'iconContainer'}>
						<DynamicFontAwesomeIcon 
							iconName={fileIcon.iconName}
							className={`${styles.fileIcon || styles.fileIconFallback || 'fileIcon'} ${styles[fileIcon.className] || fileIcon.className}`}
						/>
					</div>
				)}
				
				{/* Error overlay */}
				{(validationError || uploadError) && (
					<div 
						className={styles.fileError || styles.fileErrorFallback || 'fileError'}>
						{validationError || uploadError}
					</div>
				)}
				
				{/* Status icon/progress (top left) */}
				<div 
					className={styles.fileStatus || styles.fileStatusFallback || 'fileStatus'}>
					{renderStatus()}
				</div>
				
				{/* Retry button (below status, only for upload errors) */}
				{uploadStatus === 'error' && (
					<button 
						className={styles.retryButton}
						onClick={retryUpload}
						title="Upload wiederholen"
						aria-label="Upload wiederholen"
					>
						<DynamicFontAwesomeIcon iconName="redo" />
					</button>
				)}
				
				{/* Action buttons (top right) */}
				<div 
					className={styles.fileActions || styles.fileActionsFallback || 'fileActions'}>
					{/* Preview button - always available with blob URL as native <a> element with download */}
					<a 
						href={blobUrl}
						download={fileObject.file.name} // Always set download attribute for all file types
						target="_blank" // Open in new tab for better UX
						rel="noopener noreferrer" // Security best practice for target="_blank"
						className={styles.actionButton || styles.actionButtonFallback || 'actionButton'}
						title="Datei herunterladen"
						aria-label="Datei herunterladen"
					>
						<DynamicFontAwesomeIcon iconName="eye" />
					</a>
					<button 
						className={styles.actionButton || styles.actionButtonFallback || 'actionButton'}
						onClick={handleRemove}
						title="Datei entfernen"
						aria-label="Datei entfernen"
					>
						<DynamicFontAwesomeIcon iconName="trash-alt" />
					</button>
				</div>
				
				{/* Filename (bottom) */}
				<div 
					className={styles.fileName || styles.fileNameFallback || 'fileName'} 
					ref={fileNameRef}>
					<span className="marquee-content">
						{fileObject.file.name}
					</span>
				</div>
			</div>
		</div>
	);
};

export default FileItem;
